import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Launch as LaunchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetStaticCategoriesQuery } from '@/services/api';
import type { CategoryInfo } from '@/types/api';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

type SortField = 'name' | 'id' | 'latest_version' | 'last_updated' | 'property_count';
type SortDirection = 'asc' | 'desc';

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [isSearching, setIsSearching] = useState(false);
  const { language } = useSelector((state: RootState) => state.ui);

  // Fetch all categories from static CDN file (fast!)
  const {
    data: allCategories,
    error,
    isLoading,
  } = useGetStaticCategoriesQuery();

  const categories = allCategories || [];

  // Show loading state when search term changes
  useEffect(() => {
    if (searchTerm) {
      setIsSearching(true);
      const timer = setTimeout(() => {
        setIsSearching(false);
      }, 150);
      return () => clearTimeout(timer);
    } else {
      setIsSearching(false);
    }
  }, [searchTerm]);

  // Memoized filtered and sorted categories
  const filteredAndSortedCategories = useMemo(() => {
    return categories
      .filter((category) => {
        const searchLower = searchTerm.toLowerCase();

        return (category.name_en || '').toLowerCase().includes(searchLower) ||
               (category.name_ru || '').toLowerCase().includes(searchLower) ||
               (category.id || '').toLowerCase().includes(searchLower);
      })
      .sort((a, b) => {
        let aValue: any = a[sortField];
        let bValue: any = b[sortField];

        // Handle different data types
        if (sortField === 'last_updated') {
          aValue = new Date(aValue || 0).getTime();
          bValue = new Date(bValue || 0).getTime();
        } else if (sortField === 'property_count') {
          aValue = aValue || 0;
          bValue = bValue || 0;
        } else if (sortField === 'name') {
          // Use localized names for sorting
          aValue = language === 'ru' ? a.name_ru : a.name_en;
          bValue = language === 'ru' ? b.name_ru : b.name_en;
          aValue = (aValue || '').toLowerCase();
          bValue = (bValue || '').toLowerCase();
        } else if (typeof aValue === 'string') {
          aValue = (aValue || '').toLowerCase();
          bValue = (bValue || '').toLowerCase();
        }

        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
  }, [categories, searchTerm, sortField, sortDirection, language]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/category/${categoryId}`);
  };

  if (error) {
    const errorMessage = 'data' in error && error.data 
      ? (error.data as any).message || 'Failed to load categories'
      : 'Failed to load categories';
    
    return (
      <Box sx={{ p: 3, width: '100%', maxWidth: 'none', minWidth: '100%' }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {language === 'en' ? 'Error Loading Categories' : 'Ошибка загрузки категорий'}
          </Typography>
          <Typography variant="body2">
            {errorMessage}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%', maxWidth: 'none', minWidth: '100%' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {language === 'en' ? 'Category Playbooks' : 'Плейбуки категорий'}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {language === 'en' 
            ? 'Browse and explore category playbooks that define parsing rules for different product categories.'
            : 'Просмотрите и изучите плейбуки категорий, которые определяют правила парсинга для различных категорий продуктов.'}
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={language === 'en' ? 'Search categories...' : 'Поиск категорий...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading || isSearching ? (
                  <CircularProgress size={20} />
                ) : searchTerm ? (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 500, mb: 3 }}
        />
      </Box>

      {/* Categories Table */}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  <strong>{language === 'en' ? 'Category Name' : 'Название категории'}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'id'}
                  direction={sortField === 'id' ? sortDirection : 'asc'}
                  onClick={() => handleSort('id')}
                >
                  <strong>{language === 'en' ? 'Category ID' : 'ID категории'}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'latest_version'}
                  direction={sortField === 'latest_version' ? sortDirection : 'asc'}
                  onClick={() => handleSort('latest_version')}
                >
                  <strong>{language === 'en' ? 'Version' : 'Версия'}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'property_count'}
                  direction={sortField === 'property_count' ? sortDirection : 'asc'}
                  onClick={() => handleSort('property_count')}
                >
                  <strong>{language === 'en' ? 'Properties Count' : 'Количество свойств'}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <strong>{language === 'en' ? 'Keywords' : 'Ключевые слова'}</strong>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'last_updated'}
                  direction={sortField === 'last_updated' ? sortDirection : 'asc'}
                  onClick={() => handleSort('last_updated')}
                >
                  <strong>{language === 'en' ? 'Last Updated' : 'Последнее обновление'}</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <strong>{language === 'en' ? 'Actions' : 'Действия'}</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="30%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                  <TableCell><Skeleton variant="rectangular" width={32} height={32} /></TableCell>
                </TableRow>
              ))
            ) : filteredAndSortedCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Alert severity="info">
                    {searchTerm 
                      ? (language === 'en' ? 'No categories match your search.' : 'Категории не найдены по вашему запросу.')
                      : (language === 'en' ? 'No categories found.' : 'Категории не найдены.')}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedCategories.map((category: CategoryInfo) => (
                <TableRow 
                  key={category.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {language === 'ru' ? category.name_ru : category.name_en}
                    </Typography>
                    {language === 'ru' && category.name_en !== category.name_ru && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {category.name_en}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {category.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={category.latest_version}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {category.property_count}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(() => {
                        const keywords = category.keywords[language] || category.keywords.en || [];
                        
                        return (
                          <>
                            {keywords.slice(0, 3).map((keyword, index) => (
                              <Chip
                                key={index}
                                label={keyword}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            ))}
                            {keywords.length > 3 && (
                              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                                +{keywords.length - 3} {language === 'en' ? 'more' : 'ещё'}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {category.last_updated ? (
                      <Typography variant="body2">
                        {new Date(category.last_updated).toISOString().split('T')[0]}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <LaunchIcon 
                      color="primary" 
                      sx={{ fontSize: 20, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(category.id);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary */}
      {!isLoading && categories.length > 0 && (
        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {language === 'en'
              ? `Showing ${filteredAndSortedCategories.length} of ${categories.length} categories`
              : `Показано ${filteredAndSortedCategories.length} из ${categories.length} категорий`}
            {searchTerm && (language === 'en' ? ` matching "${searchTerm}"` : ` соответствующих "${searchTerm}"`)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CategoriesPage;